import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { AsyncProgramPPQContextProvider } from './AsyncProgramPPQ/AsyncProgramPPQContext';
import { DynamicQuizModalContextProvider } from './DynamicQuizModalContext';
import { PrePurchaseQuizContextProvider } from './PrePurchaseQuiz/PrePurchaseQuizContext';

type MDPModalContextType = {
  customUrl?: string;
  hashId?: string;
  isMDPModalAvailable: boolean;
  open: boolean;
  openHandler: Dispatch<SetStateAction<boolean>>;
  setCustomUrl: Dispatch<SetStateAction<string | undefined>>;
  setIsMDPModalAvailable: Dispatch<SetStateAction<boolean>>;
};

const contextInitialState = {
  open: false,
  openHandler: () => {},
  setIsMDPModalAvailable: () => {},
  setCustomUrl: () => {},
  isMDPModalAvailable: false,
};

const MDPModalContext = createContext<MDPModalContextType>(contextInitialState);

// VDP Modal Context
export const useMDPModalContext = (): MDPModalContextType => {
  const contextValue = useContext(MDPModalContext);

  if (contextValue === undefined) {
    throw new Error(
      'Expected a useMDPModalContext in the React tree to set the context value',
    );
  }

  return contextValue;
};

type MDPModalProps = {
  children: ReactNode;
  hashId?: string;
  /**
   * Hide the pre-purchase quiz controls (e.g. the close button) from the first
   * render. Used by the dedicated full-page quiz so the controls never flash in.
   */
  hideQuizControls?: boolean;
};

export const MDPModalContextProvider: FC<MDPModalProps> = (props) => {
  const { children, hashId, hideQuizControls = false } = props;
  const [open, openHandler] = useState(false);
  const [customUrl, setCustomUrl] = useState<string>();
  const [isMDPModalAvailable, setIsMDPModalAvailable] = useState(false);

  return (
    <MDPModalContext.Provider
      value={{
        customUrl,
        setCustomUrl,
        hashId,
        open,
        openHandler,
        isMDPModalAvailable,
        setIsMDPModalAvailable,
      }}
    >
      {/* Reusing this context that already wraps the PDPs */}
      <PrePurchaseQuizContextProvider
        initialAreControlsVisible={!hideQuizControls}
      >
        <AsyncProgramPPQContextProvider>
          <DynamicQuizModalContextProvider>
            {children}
          </DynamicQuizModalContextProvider>
        </AsyncProgramPPQContextProvider>
      </PrePurchaseQuizContextProvider>
    </MDPModalContext.Provider>
  );
};

export const formatHashId = (hashId: string | undefined): string => {
  let formattedHash = `#${hashId?.replace('#', '') || ''}`;
  return formattedHash;
};
